/**
 * Constructors for the linkage topologies the synthesizer supports, plus random
 * feasible generators used to seed multi-start optimization.
 */

import { MechanismSpec } from "./types.js";
import { circleCirclePick, dist2, type P2 } from "./geometry.js";
import { traceCoupler } from "./trace.js";
import type { Rng } from "../synthesis/rng.js";

/** Fraction of a full turn that assembles on a single circuit. */
export function assemblyFraction(m: MechanismSpec, samples = 36): number {
  return traceCoupler(m, { samples, maxIterations: 30 }).convergedFraction;
}

export interface FourBarInput {
  /** Fixed crank pivot O2. */
  ground2: P2;
  /** Fixed rocker pivot O4. */
  ground4: P2;
  /** Crank (input link) length r2. */
  crank: number;
  /** Coupler length r3 (joint A to joint B). */
  coupler: number;
  /** Rocker length r4 (joint B to O4). */
  rocker: number;
  /** Traced point in the coupler's local frame (origin A, +x toward B). */
  couplerPoint: P2;
  /** Crank angle offset. */
  phase?: number;
  dir?: 1 | -1;
}

/**
 * Planar four-bar (Grashof or not). Joint order: [O2, O4, A(driven), B(free)].
 * Bars: coupler A-B, rocker O4-B. The coupler bar carries the traced point.
 */
export function fourBar(input: FourBarInput): MechanismSpec {
  const phase = input.phase ?? 0;
  const dir = input.dir ?? 1;
  const A: P2 = [
    input.ground2[0] + input.crank * Math.cos(phase),
    input.ground2[1] + input.crank * Math.sin(phase),
  ];
  const guessB = circleCirclePick(
    A,
    input.coupler,
    input.ground4,
    input.rocker,
    [
      (A[0] + input.ground4[0]) / 2 + 0.001,
      (A[1] + input.ground4[1]) / 2 + input.coupler * 0.5,
    ],
  );
  return {
    topology: "fourbar",
    grounds: [input.ground2, input.ground4],
    driver: { base: 0, length: input.crank, phase, dir },
    free: [{ guess: guessB }],
    bars: [
      [2, 3, input.coupler],
      [1, 3, input.rocker],
    ],
    coupler: { bar: 0, u: input.couplerPoint[0], v: input.couplerPoint[1] },
  };
}

export interface SixBarInput {
  ground2: P2;
  ground4: P2;
  ground6: P2;
  crank: number;
  /** Coupler ternary link side A-B. */
  couplerAB: number;
  rocker: number;
  /** Coupler ternary link sides D-A and D-B (define point D rigidly). */
  couplerDA: number;
  couplerDB: number;
  /** Link 5, from D to C. */
  link5: number;
  /** Link 6, from C to O6. */
  link6: number;
  /** Traced point in link-5's local frame (origin D, +x toward C). */
  couplerPoint: P2;
  phase?: number;
  dir?: 1 | -1;
}

/**
 * Stephenson-III-style six-bar: a four-bar loop (O2-A-B-O4) whose coupler is a
 * ternary link A-B-D, with a dyad D-C-O6 hanging off point D. The extra loop
 * gives markedly richer coupler curves than a four-bar.
 *
 * Joint order: [O2, O4, O6, A(driven), B(free), D(free), C(free)].
 * Bars (6, matching 3 free joints): A-B, B-O4, D-A, D-B, D-C, C-O6.
 */
export function sixBarStephenson(input: SixBarInput): MechanismSpec {
  const phase = input.phase ?? 0;
  const dir = input.dir ?? 1;
  const A: P2 = [
    input.ground2[0] + input.crank * Math.cos(phase),
    input.ground2[1] + input.crank * Math.sin(phase),
  ];
  const B = circleCirclePick(A, input.couplerAB, input.ground4, input.rocker, [
    (A[0] + input.ground4[0]) / 2,
    (A[1] + input.ground4[1]) / 2 + input.couplerAB * 0.4,
  ]);
  const D = circleCirclePick(A, input.couplerDA, B, input.couplerDB, [
    (A[0] + B[0]) / 2,
    (A[1] + B[1]) / 2 + input.couplerDA * 0.6,
  ]);
  const C = circleCirclePick(D, input.link5, input.ground6, input.link6, [
    (D[0] + input.ground6[0]) / 2,
    (D[1] + input.ground6[1]) / 2,
  ]);
  return {
    topology: "stephenson3",
    grounds: [input.ground2, input.ground4, input.ground6],
    driver: { base: 0, length: input.crank, phase, dir },
    free: [{ guess: B }, { guess: D }, { guess: C }],
    bars: [
      [3, 4, input.couplerAB], // A-B
      [4, 1, input.rocker], // B-O4
      [5, 3, input.couplerDA], // D-A
      [5, 4, input.couplerDB], // D-B
      [5, 6, input.link5], // D-C
      [6, 2, input.link6], // C-O6
    ],
    coupler: { bar: 4, u: input.couplerPoint[0], v: input.couplerPoint[1] },
  };
}

// ---------------------------------------------------------------------------
// Grashof condition
// ---------------------------------------------------------------------------

export interface GrashofInfo {
  /** s + l vs p + q. Negative => Grashof (at least one link fully rotates). */
  margin: number;
  isGrashof: boolean;
  /** True if the input crank can make a full revolution. */
  crankRotates: boolean;
  lengths: { ground: number; crank: number; coupler: number; rocker: number };
}

/** Grashof analysis for a four-bar spec. */
export function grashof(m: MechanismSpec): GrashofInfo {
  const ground = dist2(m.grounds[0], m.grounds[1]);
  const crank = m.driver.length;
  const coupler = m.bars[0][2];
  const rocker = m.bars[1][2];
  const links = [ground, crank, coupler, rocker];
  const s = Math.min(...links);
  const l = Math.max(...links);
  const rest = links.reduce((a, b) => a + b, 0) - s - l;
  const margin = s + l - rest;
  const isGrashof = margin < 0;
  // Crank fully rotates iff it is the shortest link (or tie) in a Grashof chain.
  const crankRotates = isGrashof && crank <= s + 1e-9;
  return {
    margin,
    isGrashof,
    crankRotates,
    lengths: { ground, crank, coupler, rocker },
  };
}

// ---------------------------------------------------------------------------
// Random feasible generators for multi-start optimization
// ---------------------------------------------------------------------------

/** A random four-bar whose crank is likely to fully rotate, centered on origin. */
export function randomFourBar(rng: Rng, scale = 1): MechanismSpec {
  for (let attempt = 0; attempt < 200; attempt++) {
    const gx = rng.range(0.6, 1.8) * scale;
    const ground2: P2 = [-gx / 2 + rng.range(-0.2, 0.2), rng.range(-0.3, 0.3)];
    const ground4: P2 = [gx / 2 + rng.range(-0.2, 0.2), rng.range(-0.3, 0.3)];
    const crank = rng.range(0.15, 0.45) * scale;
    const coupler = rng.range(0.6, 1.6) * scale;
    const rocker = rng.range(0.5, 1.4) * scale;
    const spec = fourBar({
      ground2,
      ground4,
      crank,
      coupler,
      rocker,
      couplerPoint: [rng.range(0.1, 0.9) * coupler, rng.range(-0.7, 0.7) * scale],
      phase: rng.range(0, Math.PI * 2),
    });
    if (grashof(spec).crankRotates) return spec;
  }
  // Fallback: a known-good Grashof crank-rocker.
  return fourBar({
    ground2: [-0.5, 0],
    ground4: [0.5, 0],
    crank: 0.25,
    coupler: 1.0,
    rocker: 0.9,
    couplerPoint: [0.5, 0.4],
  });
}

/** A random Stephenson-III six-bar with a rotating input crank. */
export function randomSixBar(rng: Rng, scale = 1): MechanismSpec {
  for (let attempt = 0; attempt < 300; attempt++) {
    const ground2: P2 = [rng.range(-0.9, -0.2) * scale, rng.range(-0.3, 0.3) * scale];
    const ground4: P2 = [rng.range(0.2, 0.9) * scale, rng.range(-0.3, 0.3) * scale];
    const ground6: P2 = [rng.range(-0.4, 0.4) * scale, rng.range(0.6, 1.4) * scale];
    const crank = rng.range(0.12, 0.32) * scale;
    const couplerAB = rng.range(0.6, 1.3) * scale;
    const rocker = rng.range(0.5, 1.2) * scale;
    const couplerDA = rng.range(0.4, 0.9) * scale;
    const couplerDB = rng.range(0.4, 0.9) * scale;
    const link5 = rng.range(0.4, 1.1) * scale;
    const link6 = rng.range(0.4, 1.1) * scale;
    const spec = sixBarStephenson({
      ground2,
      ground4,
      ground6,
      crank,
      couplerAB,
      rocker,
      couplerDA,
      couplerDB,
      link5,
      link6,
      couplerPoint: [rng.range(0.1, 0.9) * link5, rng.range(-0.5, 0.5) * scale],
      phase: rng.range(0, Math.PI * 2),
    });
    // Reuse the four-bar Grashof test on the primary loop.
    const primary = fourBar({
      ground2,
      ground4,
      crank,
      coupler: couplerAB,
      rocker,
      couplerPoint: [0, 0],
    });
    // Require the primary loop to rotate AND the full six-bar to assemble on a
    // single circuit for (almost) the whole turn.
    if (grashof(primary).crankRotates && assemblyFraction(spec) > 0.92) return spec;
  }
  return sixBarStephenson({
    ground2: [-0.5, 0],
    ground4: [0.5, 0],
    ground6: [0, 1.0],
    crank: 0.22,
    couplerAB: 1.0,
    rocker: 0.9,
    couplerDA: 0.6,
    couplerDB: 0.6,
    link5: 0.7,
    link6: 0.7,
    couplerPoint: [0.35, 0.2],
  });
}
